import React from "react";
import Switch from "./Switch";
import "./ProfileSettingsCard.css";
import { updatePin } from "../../../../../../config/Services";
import { PosCustomButton } from "../../../../../../customComponents/PosCustomButton/PosCustomButton";
import PosCustomIcon from "../../../../../../customComponents/PosCustomIcon/PosCustomIcon";
import { BellIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
const ProfileSettingsCard = ({ type, data = {}, onChange, onAction, isChangingPin = false }) => {
  const handleInputChange = (field, value) => {
    if (onChange) {
      onChange(type, field, value);
    }
  };

  const handleSwitchChange = (field, checked) => {
    if (onChange) {
      onChange(type, field, checked);
    }
  };

  if (type === "identity") {
    return (
      <div className="settings-card">
        <div className="settings-card-header">
          <p className="settings-card-badge" style={{ "--badge-color": "var(--primary)" }}>
            Edit details
          </p>
          <h2 className="settings-card-title">Identity and outlet information</h2>
          <p className="settings-card-description">
            Keep operator identity, outlet information, and contact details current for billing,
            tickets, and escalations.
          </p>
        </div>
        <div className="settings-card-content grid-2-col">
          <div className="input-group">
            <label className="input-label">First name</label>
            <input
              type="text"
              className="input-field"
              value={data?.firstName || ""}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              data-testid="input-profile-first-name"
            />
          </div>
          <div className="input-group">
            <label className="input-label">Last name</label>
            <input
              type="text"
              className="input-field"
              value={data?.lastName || ""}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              data-testid="input-profile-last-name"
            />
          </div>
          <div className="input-group full-width">
            <div>
              <div style={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
                <label className="input-label">Assigned roles</label>
                <span className="status-muted">{data?.role?.split(",")?.length}/15 roles</span>
              </div>
              <p
                style={{
                  marginTop: "0.25rem",
                  fontSize: "0.875rem",
                  lineHeight: "1.25rem",
                  color: " #64748b",
                }}
              >
                Add up to 15 roles without crowding the layout. Duplicate labels are ignored
                automatically.
              </p>
              <div className="edit-role-container">
                {data?.role?.split(",")?.map((ele, ind) => {
                  return (
                    <input
                      type="text"
                      key={ind}
                      className="input-field role-input"
                      value={ele}
                      readOnly
                      style={{ backgroundColor: "white" }}
                    />
                  );
                })}
              </div>
            </div>
            {/* <label className="input-label">Role</label>
            <input
              type="text"
              className="input-field"
              value={data?.role || ""}
              onChange={(e) => handleInputChange("role", e.target.value)}
              data-testid="input-profile-role"
              readOnly
            /> */}
          </div>
          <div className="input-group">
            <label className="input-label">Outlet</label>
            <input
              type="text"
              className="input-field"
              value={localStorage.getItem("LocName") || data?.outlet}
              onChange={(e) => handleInputChange("outlet", e.target.value)}
              data-testid="input-profile-outlet"
              readOnly
            />
          </div>
          <div className="input-group">
            <label className="input-label">Email</label>
            <input
              type="email"
              className="input-field"
              value={data?.email || ""}
              onChange={(e) => handleInputChange("email", e.target.value)}
              data-testid="input-profile-email"
            />
          </div>
          <div className="input-group">
            <label className="input-label">Phone</label>
            <input
              type="tel"
              className="input-field"
              value={data?.phone || ""}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              data-testid="input-profile-phone"
            />
          </div>
          <div className="input-group">
            <label className="input-label">City / location</label>
            <input
              type="text"
              className="input-field"
              value={data?.city || ""}
              onChange={(e) => handleInputChange("city", e.target.value)}
              data-testid="input-profile-city"
              readOnly
            />
          </div>
          <div className="input-group">
            <label className="input-label">Employee code</label>
            <input
              type="text"
              className="input-field"
              value={data?.employeeCode || ""}
              onChange={(e) => handleInputChange("employeeCode", e.target.value)}
              data-testid="input-profile-employee-code"
              readOnly
            />
          </div>
          <div className="input-group">
            <label className="input-label">Joined on</label>
            <input
              type="text"
              className="input-field"
              value={data?.createdDate ? new Date(data.createdDate).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : ""}
              data-testid="input-profile-joined-on"
              readOnly
            />
          </div>
        </div>
      </div>
    );
  }

  // Notifications section with toggles
  if (type === "notifications") {
    const notificationItems = [
      {
        key: "dineInOrders",
        title: "Dine-in Orders",
        description: "Alerts for new dine-in tickets and service updates.",
        testId: "switch-notification-critical-alerts",
      },
      {
        key: "scheduledOrders",
        title: "Scheduled Orders",
        description: "Notifications for orders planned ahead of time.",
        testId: "switch-notification-ticket-updates",
      },
      {
        key: "takeawayOrders",
        title: "Takeaway Orders",
        description: "Updates for takeaway pickups and ready-to-collect orders.",
        testId: "switch-notification-inventory-blocks",
      },
      {
        key: "deliveryOrders",
        title: "Delivery Orders",
        description: "Alerts for dispatch, rider handoff, and delivery progress.",
        testId: "switch-notification-shift-summary",
      },
      {
        key: "onlineOrders",
        title: "Online Orders",
        description: "Notifications for website and marketplace order activity.",
        testId: "online-orders",
      },
    ];

    return (
      <div className="settings-card">
        <div className="settings-card-header">
          <p className="settings-card-badge" style={{ "--badge-color": "var(--violet-500)" }}>
            Notification preferences
          </p>
          <h2 className="settings-card-title">Alerts and updates</h2>
          <p className="settings-card-description">
            Decide which operational updates you want to receive during the shift and after ticket
            activity.
          </p>
        </div>
        <div className="settings-card-content">
          {notificationItems?.map((item) => (
            <div key={item?.key} className="toggle-card">
              <div>
                <p className="toggle-title">{item?.title}</p>
                <p className="toggle-description">{item?.description}</p>
              </div>
              <Switch
                checked={data[item?.key] || false}
                onChange={(checked) => handleSwitchChange(item?.key, checked)}
                data-testid={item?.testId}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Security section with password fields and toggles
  if (type === "security") {
    const securityToggles = [
      {
        key: "twoFactor",
        title: "Two-factor authentication",
        description: "Require OTP verification when signing in from a new device.",
        testId: "switch-security-two-factor",
      },
      {
        key: "loginAlerts",
        title: "Login alerts",
        description: "Send alerts whenever the account is used on a new device.",
        testId: "switch-security-login-alerts",
      },
      {
        key: "sensitiveActions",
        title: "Approval check for sensitive actions",
        description: "Require re-authentication before refunds, voids, and inventory overrides.",
        testId: "switch-security-sensitive-actions",
      },
    ];

    return (
      <div className="settings-card">
        <div className="settings-card-header">
          <p className="settings-card-badge" style={{ "--badge-color": "var(--emerald-500)" }}>
            Security settings
          </p>
          <h2 className="settings-card-title">Change Account PIN</h2>
          <p className="settings-card-description">
            Update the account PIN used for sensitive store actions in one secure verification flow.
          </p>
        </div>
        <div className="settings-card-content">
          <div className="password-section">
            {/* <div className="input-group">
              <label className="input-label">OTP</label>
              <input
                type="password"
                className="input-field"
                value={data.currentPassword || ''}
                onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                data-testid="input-security-current-password"
              />
            </div>
            <div className="input-group">
              <label className="input-label">New Pin</label>
              <input
                type="password"
                className="input-field"
                value={data.newPassword || ''}
                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                data-testid="input-security-new-password"
              />
            </div>
            <div className="input-group">
              <label className="input-label">Confirm new Pin</label>
              <input
                type="password"
                className="input-field"
                value={data.confirmPassword || ''}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                data-testid="input-security-confirm-password"
              />
            </div> */}

            <PosCustomButton
              title="Change PIN"
              variant="black-d"
              loading={isChangingPin}
              onClickFunction={() => {
                if (onAction) {
                  onAction("updatePin", data);
                }
              }}
              p="0.5rem 1rem"
              fontWeight="500"
              height="3rem"
              radius="1rem"
            />
          </div>
          {/* <div className="toggles-section">
            {securityToggles?.map((item) => (
              <div key={item?.key} className="toggle-card">
                <div>
                  <p className="toggle-title">{item?.title}</p>
                  <p className="toggle-description">{item?.description}</p>
                </div>
                <Switch
                  checked={data[item?.key] || false}
                  onChange={(checked) => handleSwitchChange(item?.key, checked)}
                  data-testid={item?.testId}
                />
              </div>
            ))}
          </div> */}
        </div>
      </div>
    );
  }

  if (type === "summary") {
    const activeNotifications = Object.values(data?.notifications || {})?.filter(Boolean)?.length;
    const activeSecurity = Object.values(data?.security || {})?.filter(Boolean)?.length;

    return (
      <div className="settings-card summary-card">
        <div className="summary-card-inner">
          <div className="summary-header">
            <div className="summary-icon">
              {/* <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
              </svg> */}
              <PosCustomIcon
                icon={<ShieldCheckIcon />}
                height={"18px"}
                width={"18px"}
                color="white"
              />
            </div>
            <div>
              <p className="summary-badge">Edit flow</p>
              <h3 className="summary-title">Focused account management</h3>
              <p className="summary-description">
                Details live on the profile overview page. This screen is reserved for making
                changes so the experience stays cleaner and easier to scan.
              </p>
            </div>
          </div>
          <div className="summary-stats">
            <div className="stat-card">
              <p className="stat-label">Notification channels</p>
              <p className="stat-value" data-testid="text-profile-edit-notification-enabled-count">
                {activeNotifications} active
              </p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Security safeguards</p>
              <p className="stat-value" data-testid="text-profile-edit-security-enabled-count">
                {activeSecurity} enabled
              </p>
            </div>
            <div className="stat-card wide">
              <div className="info-row">
                {/* <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10.268 21a2 2 0 0 0 3.464 0"></path>
                  <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326"></path>
                </svg> */}
                <PosCustomIcon icon={<BellIcon />} height={"18px"} width={"18px"} color="white" />
                <p className="info-text">
                  You can review the saved profile anytime from the details page.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ProfileSettingsCard;
